import React, { useEffect, useRef, useState } from 'react';

interface ListenButtonProps {
  url: string;
  listenLabel: string;
  pauseLabel: string;
  /** Pastille avec libellé (fiche) ou icône seule (vignette de la liste). */
  variant: 'pill' | 'icon';
}

// Une seule lecture à la fois sur la page : lancer une vignette coupe la
// précédente, sans quoi deux passages se liraient l'un sur l'autre.
let current: HTMLAudioElement | null = null;

const HeadphonesIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
  </svg>
);

const PauseIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <rect x="5" y="3" width="5" height="18" />
    <rect x="14" y="3" width="5" height="18" />
  </svg>
);

/**
 * Écoute de la lecture à plusieurs voix du passage commenté
 * (`lecture_audio_url`). `preload="none"` : rien ne se télécharge tant qu'on
 * n'a pas appuyé. L'avancement se lit sur le bouton lui-même — la pastille se
 * remplit, l'icône s'entoure d'un anneau —, sans barre de lecture en plus.
 */
const ListenButton: React.FC<ListenButtonProps> = ({ url, listenLabel, pauseLabel, variant }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => () => {
    // Quitter la page coupe le son.
    audioRef.current?.pause();
  }, []);

  const toggle = (e: React.MouseEvent) => {
    // La vignette entière est un lien : l'appui ne doit pas ouvrir la fiche.
    e.preventDefault();
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      if (current && current !== audio) current.pause();
      current = audio;
      // Un refus du navigateur ne doit rien casser : le bouton reste « Écouter ».
      audio.play().catch(() => setPlaying(false));
    } else {
      audio.pause();
    }
  };

  const label = playing ? pauseLabel : listenLabel;
  const pct = progress * 100;

  return (
    <>
      <audio
        ref={audioRef}
        src={url}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onTimeUpdate={e => {
          const a = e.currentTarget;
          if (a.duration) setProgress(a.currentTime / a.duration);
        }}
      />
      {variant === 'pill' ? (
        <button
          type="button"
          onClick={toggle}
          className="flex items-center gap-1 text-white text-[0.75rem] font-bold px-3 py-0.5 rounded-full hover:opacity-90 transition-opacity"
          style={{ background: `linear-gradient(to right, #92400e ${pct}%, #d97706 ${pct}%)` }}
          aria-label={label}
        >
          {playing ? <PauseIcon size={10} /> : <HeadphonesIcon size={12} />}
          {label}
        </button>
      ) : (
        <button
          type="button"
          onClick={toggle}
          className="relative z-[2] h-7 w-7 flex items-center justify-center rounded-full text-amber-700 hover:bg-amber-100 transition-colors"
          aria-label={label}
          title={label}
        >
          {(playing || progress > 0) && (
            <svg className="absolute inset-0 -rotate-90" viewBox="0 0 28 28">
              <circle cx="14" cy="14" r="12.5" fill="none" stroke="#fde68a" strokeWidth="2" />
              <circle
                cx="14" cy="14" r="12.5" fill="none" stroke="#d97706" strokeWidth="2"
                strokeDasharray={2 * Math.PI * 12.5}
                strokeDashoffset={2 * Math.PI * 12.5 * (1 - progress)}
              />
            </svg>
          )}
          {playing ? <PauseIcon size={11} /> : <HeadphonesIcon size={15} />}
        </button>
      )}
    </>
  );
};

export default ListenButton;
